/**
 * calendarDates — pure date math for the content calendar grid.
 *
 * Uses dayjs (already a transitive dep via @mui/x-date-pickers). Avoids
 * DST-sensitive local-arithmetic by doing all week math in UTC and
 * expecting callers to pass ISO strings or Date objects.
 *
 * Anchor convention: Monday is weekday 0 (index 0..6 = Mon..Sun).
 * This matches ContentPost.weekdayIndex / StrategyPlatformCadence.preferredWeekdays.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(utc);
dayjs.extend(isoWeek);

export interface CalendarWeek {
  /** 0-based index within the current window. */
  index: number;
  /** Monday 00:00 UTC of the ISO week. */
  startDate: Date;
  /** Sunday 23:59:59.999 UTC of the ISO week. */
  endDate: Date;
  /** Human label, e.g. "Apr 06 – Apr 12". */
  label: string;
}

/** Monday of the ISO week containing the given date. */
export function startOfIsoWeek(date: Date | string): Date {
  return dayjs.utc(date).startOf('isoWeek').toDate();
}

/** Sunday of the ISO week containing the given date. */
export function endOfIsoWeek(date: Date | string): Date {
  return dayjs.utc(date).endOf('isoWeek').toDate();
}

/**
 * Build N weeks starting from the ISO-week Monday of `windowStart`.
 * The last entry's endDate may overshoot the user's chosen windowEnd —
 * we always render whole weeks to keep the grid visually consistent.
 */
export function buildWeeks(windowStart: Date, weekCount: number): CalendarWeek[] {
  const weeks: CalendarWeek[] = [];
  let cursor = dayjs.utc(windowStart).startOf('isoWeek');
  for (let i = 0; i < weekCount; i++) {
    const end = cursor.endOf('isoWeek');
    weeks.push({
      index: i,
      startDate: cursor.toDate(),
      endDate: end.toDate(),
      label: `${cursor.format('MMM DD')} – ${end.format('MMM DD')}`,
    });
    cursor = cursor.add(1, 'week');
  }
  return weeks;
}

/** Number of ISO weeks between two dates, inclusive on both ends. */
export function weeksBetween(start: Date, end: Date): number {
  const a = dayjs.utc(start).startOf('isoWeek');
  const b = dayjs.utc(end).endOf('isoWeek');
  return b.diff(a, 'week') + 1;
}

/** Day key used by the grid to match publications to cells. */
export function dayKey(date: Date | string): string {
  return dayjs.utc(date).format('YYYY-MM-DD');
}

/** Index (0..6 Mon..Sun) of the given date's weekday. */
export function weekdayIndexOf(date: Date | string): number {
  return dayjs.utc(date).isoWeekday() - 1;
}

/** All 7 day keys of a given week, Mon..Sun. */
export function daysOfWeek(week: CalendarWeek): Array<{ key: string; date: Date; weekdayIndex: number }> {
  const out: Array<{ key: string; date: Date; weekdayIndex: number }> = [];
  for (let i = 0; i < 7; i++) {
    const d = dayjs.utc(week.startDate).add(i, 'day');
    out.push({ key: d.format('YYYY-MM-DD'), date: d.toDate(), weekdayIndex: i });
  }
  return out;
}

/**
 * Replace the date portion of `scheduledAt` with the given target day,
 * preserving the HH:mm:ss.SSS time-of-day. Used when a chip is dragged
 * to a different cell — we want to keep its time-of-day stable.
 */
export function moveScheduledAtToDay(scheduledAtIso: string, targetDay: Date): string {
  const orig = dayjs.utc(scheduledAtIso);
  const target = dayjs.utc(targetDay);
  return target
    .hour(orig.hour())
    .minute(orig.minute())
    .second(orig.second())
    .millisecond(orig.millisecond())
    .toISOString();
}
